import React, { FC, memo, useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';

import { formatBNWithCommas } from '@utils/formaters.utils';

import Box from '@components/common/Box';
import Button from '@components/common/Button';
import Table from '@components/common/Table';
import { ROW_HEIGHT } from '@components/common/Table/table.constants';
import { getCellStyles } from '@components/common/Table/table.helpers';
import FarmDetails from '@components/vaults/FarmDetails';
import RiskLeveler from '@components/vaults/RiskLeveler';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { Size, TableVariant, Variant } from '@constants/common/ui.constants';
import { V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';
import {
  CREATE_VAULT_HEADER,
  CreateVaultsKeys,
} from '@constants/vaults/vault.constants';

import { TokensDto } from '@dto/vaults/tokens.dto';
import { FarmDto } from '@dto/vaults/vault-factory.dto';

import { FarmCard } from '@app-types/vaults/farm.types';

import useStyles from './styles.module';

interface Props {
  selectedFarms: FarmCard[];
  farms: FarmDto[];
  isPending: boolean;
  totalCount: number;
  isSideBarView?: boolean;
  isDisabledSelect: boolean;
  tokens: TokensDto;
  onSelectFarm: (value: FarmDto) => void;
  tableHeight?: number;
  className?: string;
}

const AllFarms: FC<Props> = ({
  isDisabledSelect,
  farms,
  isPending,
  totalCount,
  tokens,
  selectedFarms,
  onSelectFarm,
  isSideBarView = false,
  tableHeight = 500,
  className = '',
}) => {
  const { classes } = useStyles();

  const filteredFarms = useMemo(() => {
    if (!selectedFarms.length) return farms;

    return farms.filter(
      (farm) =>
        !selectedFarms.some((selectedFarm) => selectedFarm.farm.id === farm.id),
    );
  }, [farms, selectedFarms]);

  const columns = useMemo<ColumnDef<FarmDto>[]>(
    () => [
      {
        size: 270,
        id: CreateVaultsKeys.Farm,
        accessorKey: CreateVaultsKeys.Farm,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Farm],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => {
          const {
            chainId,
            stakingToken,
            stakingTokenType,
            dateOfAdd,
            poolV3Fee,
            poolV3,
            enabled,
            dexFactory,
            origin,
          } = row;

          const address = V3_TOKENS_TYPE.includes(stakingTokenType)
            ? poolV3
            : stakingToken;
          const factoryTokens = tokens.farm[chainId];
          const stakeToken = factoryTokens[address as string];
          const enabledFarmData = enabled
            ? { origin, dexFactoryName: dexFactory.name }
            : undefined;

          return (
            <div className={classes.farm}>
              <FarmDetails
                withChainLabel
                chainId={chainId}
                stakingToken={stakeToken}
                isPoolV3PositionOutRange={false}
                enabledFarmData={enabledFarmData}
                farmTagsData={{ dateOfAdd, poolV3Fee }}
              />
            </div>
          );
        },
      },

      {
        size: 130,
        id: CreateVaultsKeys.Exchange,
        accessorKey: CreateVaultsKeys.Exchange,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Exchange],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => row.dexFactory.name,
      },
      {
        size: 130,
        id: CreateVaultsKeys.Liquidity,
        accessorKey: CreateVaultsKeys.Liquidity,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Liquidity],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => `$${formatBNWithCommas(row.tvlUsd, 2)}`,
      },
      {
        size: isSideBarView ? 120 : 130,
        id: CreateVaultsKeys.APR,
        accessorKey: CreateVaultsKeys.APR,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.APR],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) =>
          `${formatBNWithCommas(Number(row.apr) * PERCENT_DIVIDER, 2, undefined, true)}%`,
      },
      {
        size: isSideBarView ? 60 : 110,
        id: CreateVaultsKeys.Risk,
        accessorKey: CreateVaultsKeys.Risk,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Risk],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => (
          <RiskLeveler value={row.riskLevel} className={classes.riskLeveler} />
        ),
      },
      {
        id: CreateVaultsKeys.Action,
        accessorKey: CreateVaultsKeys.Action,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Action],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (farm) => (
          <div className={classes.btnBox}>
            <Button
              size={Size.Small}
              variant={Variant.Secondary}
              onClick={() => onSelectFarm(farm)}
              disabled={isDisabledSelect}
              key={`${farm.id}`}
            >
              Select
            </Button>
          </div>
        ),
      },
    ],
    [isSideBarView, farms, isDisabledSelect, tokens, onSelectFarm],
  );

  return (
    <Box
      title="All Farms"
      classNameChildren={classes.box}
      className={className}
    >
      <Table
        data={filteredFarms}
        columns={columns}
        isLoading={isPending}
        rowHeight={ROW_HEIGHT}
        tableHeight={tableHeight}
        totalRowCount={totalCount}
        variant={TableVariant.Secondary}
        pinnedRightColumn={[CreateVaultsKeys.Action]}
      />
    </Box>
  );
};

export default memo(AllFarms);
